import { AlertCircle, Bot, Plus, Rocket, Upload, Wrench } from "lucide-react";
import * as React from "react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

import {
  AGENT_TEMPLATES,
  attachKnowledge,
  attachSkill,
  createAtelierAgent,
  deployAtelierRuntime,
  listAtelierSkills,
  provisionAgents,
  type AtelierAgent,
  type AtelierSkill,
  type ProvisionStatus,
} from "@/services/atelier";

export function AgentPanel() {
  const [skillsByAgent, setSkillsByAgent] = React.useState<Record<string, AtelierSkill[]>>({});
  const [createdAgents, setCreatedAgents] = React.useState<AtelierAgent[]>([]);
  const [selectedTemplate, setSelectedTemplate] = React.useState<string>("");
  const [agentName, setAgentName] = React.useState("");
  const [creating, setCreating] = React.useState(false);
  const [deployingId, setDeployingId] = React.useState<string | null>(null);
  const [attachingId, setAttachingId] = React.useState<string | null>(null);
  const [provisioning, setProvisioning] = React.useState(false);
  const [provisionStatus, setProvisionStatus] = React.useState<ProvisionStatus | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  const loadAgentSkills = React.useCallback(async (agentId: string) => {
    try {
      const data = await listAtelierSkills(agentId);
      setSkillsByAgent((prev) => ({ ...prev, [agentId]: data }));
    } catch (err) {
      setSkillsByAgent((prev) => ({ ...prev, [agentId]: [] }));
      setError(extractErrorMessage(err, "无法加载该 Agent 的技能列表。"));
    }
  }, []);

  const handleCreate = async () => {
    if (!selectedTemplate) return;
    setCreating(true);
    setError(null);
    try {
      const agent = await createAtelierAgent(selectedTemplate, agentName || undefined);
      setCreatedAgents((prev) => [agent, ...prev]);
      setAgentName("");
      if (agent.id) {
        void loadAgentSkills(agent.id);
      }
    } catch (err) {
      setError(extractErrorMessage(err, "创建 Agent 失败。"));
    } finally {
      setCreating(false);
    }
  };

  const handleDeploy = async (agentId: string) => {
    setDeployingId(agentId);
    setError(null);
    try {
      const result = await deployAtelierRuntime(agentId);
      if (result.deployed === false) {
        setNotice(`部署未完成（best-effort）：${result.error ?? "运行时未就绪"}`);
      } else {
        setNotice("运行时已部署。");
      }
    } catch (err) {
      setError(extractErrorMessage(err, "部署 Agent 运行时失败。"));
    } finally {
      setDeployingId(null);
    }
  };

  const handleAttachSkill = async (agentId: string) => {
    const skillName = window.prompt("请输入要挂载的技能名称（同名自动复用）");
    if (!skillName?.trim()) return;
    setAttachingId(agentId);
    setError(null);
    try {
      const result = await attachSkill(agentId, {
        name: skillName.trim(),
        display_name: skillName.trim(),
        description: "",
        definition: {},
      });
      setNotice(`技能「${result.display_name ?? result.name ?? skillName}」已挂载。`);
      void loadAgentSkills(agentId);
    } catch (err) {
      setError(extractErrorMessage(err, "挂载技能失败。"));
    } finally {
      setAttachingId(null);
    }
  };

  const handleAttachKnowledge = async (agentId: string) => {
    try {
      const result = await attachKnowledge(agentId);
      if (result.reachable) {
        setNotice(result.degraded ? "知识查询链路可用（关键词降级模式）。" : "知识查询链路可用。");
      } else {
        setNotice(`知识查询暂不可用：${result.error ?? "Atelier 未提供知识检索"}`);
      }
    } catch (err) {
      setError(extractErrorMessage(err, "挂载知识库失败。"));
    }
  };

  const handleProvision = async () => {
    setProvisioning(true);
    setError(null);
    try {
      const result = await provisionAgents();
      setProvisionStatus({ provisioned: true, ...result });
    } catch (err) {
      setError(extractErrorMessage(err, "同步内置 Agent 到 Atelier 失败（需配置 ATELIER_ADMIN_TOKEN）。"));
    } finally {
      setProvisioning(false);
    }
  };

  return (
    <Card className="flex h-full flex-col">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Bot className="h-4 w-4" />
            Agent 模板（Atelier）
          </span>
          <Button size="sm" variant="outline" onClick={() => void handleProvision()} disabled={provisioning}>
            <Upload className={cn("mr-1 h-3.5 w-3.5", provisioning && "animate-pulse")} />
            {provisioning ? "同步中..." : "同步内置 Agent"}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 overflow-auto">
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-medium">Atelier 服务异常</p>
              <p className="text-xs opacity-90">{error}</p>
            </div>
          </div>
        )}
        {notice && (
          <div className="flex items-start gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm text-emerald-700 dark:text-emerald-400">
            <div className="flex-1">{notice}</div>
            <button className="text-xs opacity-70 hover:opacity-100" onClick={() => setNotice(null)}>
              关闭
            </button>
          </div>
        )}

        {provisionStatus && (
          <div className="rounded-md border p-3 text-xs">
            <h4 className="mb-1 text-sm font-medium">
              内置 Agent 同步结果（{provisionStatus.total ?? 0} 个）
            </h4>
            <div className="flex flex-wrap gap-2">
              <span className="rounded bg-emerald-500/15 px-2 py-0.5">新建 {provisionStatus.created?.length ?? 0}</span>
              <span className="rounded bg-sky-500/15 px-2 py-0.5">复用 {provisionStatus.reused?.length ?? 0}</span>
              <span className="rounded bg-destructive/15 px-2 py-0.5">失败 {provisionStatus.failed?.length ?? 0}</span>
            </div>
            {(provisionStatus.failed?.length ?? 0) > 0 && (
              <ul className="mt-2 space-y-1 text-destructive">
                {provisionStatus.failed?.map((f) => (
                  <li key={f.name}>
                    {f.name}: {f.error}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="rounded-md border p-3">
            <h4 className="mb-2 text-sm font-medium">从模板创建 Agent</h4>
            <div className="space-y-3">
              <div className="grid gap-2">
                <Label className="text-xs">选择模板</Label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => setSelectedTemplate(e.target.value)}
                  className="h-8 rounded-md border bg-background px-2 text-xs"
                >
                  <option value="">请选择...</option>
                  {AGENT_TEMPLATES.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>
              {selectedTemplate && (
                <p className="text-xs text-muted-foreground">
                  {AGENT_TEMPLATES.find((t) => t.id === selectedTemplate)?.description}
                </p>
              )}
              <div className="grid gap-2">
                <Label className="text-xs">Agent 名称（可选）</Label>
                <Input
                  value={agentName}
                  onChange={(e) => setAgentName(e.target.value)}
                  placeholder="覆盖默认名称"
                  className="h-8 text-xs"
                />
              </div>
              <Button size="sm" onClick={() => void handleCreate()} disabled={!selectedTemplate || creating}>
                <Plus className="mr-1 h-3.5 w-3.5" />
                {creating ? "创建中..." : "创建 Agent"}
              </Button>
            </div>
          </div>

          <div className="rounded-md border p-3">
            <h4 className="mb-2 flex items-center gap-2 text-sm font-medium">
              <Wrench className="h-3.5 w-3.5" />
              已创建 Agent 技能
            </h4>
            {createdAgents.length === 0 ? (
              <p className="text-sm text-muted-foreground">暂无技能或 Atelier 未连接</p>
            ) : (
              <div className="max-h-48 space-y-2 overflow-auto">
                {createdAgents.slice(0, 5).map((agent) => {
                  const skills = skillsByAgent[agent.id] ?? [];
                  return (
                    <div key={agent.id} className="rounded-md border bg-card p-2 text-xs">
                      <p className="font-medium">{agent.name || agent.id}</p>
                      {skills.length === 0 ? (
                        <p className="text-muted-foreground">暂无技能</p>
                      ) : (
                        <ul className="mt-1 space-y-0.5">
                          {skills.map((s) => (
                            <li key={s.id} className="text-muted-foreground">
                              · {s.display_name || s.name || s.id}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-1 flex-col gap-2 rounded-md border p-3">
          <h4 className="text-sm font-medium">已创建 Agent</h4>
          {createdAgents.length === 0 ? (
            <p className="py-4 text-center text-sm text-muted-foreground">尚未创建 Agent</p>
          ) : (
            <div className="space-y-2 overflow-auto">
              {createdAgents.map((agent) => (
                <div key={agent.id} className="flex items-center justify-between rounded-md border bg-card p-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium">{agent.name || agent.id}</p>
                    <p className="text-[10px] text-muted-foreground">ID: {agent.id}</p>
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => void handleAttachSkill(agent.id)}
                      disabled={attachingId === agent.id}
                    >
                      {attachingId === agent.id ? "挂载中..." : "挂载技能"}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => void handleAttachKnowledge(agent.id)}>
                      知识探活
                    </Button>
                    <Button
                      size="sm"
                      onClick={() => void handleDeploy(agent.id)}
                      disabled={deployingId === agent.id}
                    >
                      <Rocket className="mr-1 h-3.5 w-3.5" />
                      {deployingId === agent.id ? "部署中..." : "部署"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function extractErrorMessage(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "message" in error && typeof error.message === "string") {
    return error.message || fallback;
  }
  return fallback;
}
